import axios from "axios";
import useSWR, { useSWRImmut } from "./useSWR";
import { useCallback, useState } from "react";
import { cloneDeep } from "lodash";
import { resultsFunction as guidedSelling } from "../utils/resultFunctions/guidedSelling";
import { resultsFunction as guidedSellingAU } from "../utils/resultFunctions/guidedSellingAU";
import { resultsFunction as defaultResultsFunction } from "../utils/resultFunctions/defaultResultsFunction";
import {
  isEligibleWw,
  getResultsPageEli,
  getResultsJoinSequence,
  WAITLIST_REASON_IRRETRIEVABLE
} from "./eligibility";
import {
  getIsClinicEligibileUni,
  getResultsPageUni
} from "./resultFunctions/unifiedAssessment";
import { getConfigByLocale } from "../config/global";
import getSessionData from "./getSessionData";
import {
  getIsClinicEligibleUni4,
  getResultsPageUniV4
} from "./resultFunctions/unifiedV4";
import {
  getIsClinicEligibleUni3,
  getResultsPageUniV3
} from "./resultFunctions/unifiedV3";
import { updateAssessmentId } from "./account";
import { logger } from "./logger";
import { FINISHED } from "./resultFunctions/constants";
import { SECONDS_IN_THIRTY_DAYS } from "./cookies";
import { getResultsPageFoodNoise } from "./resultFunctions/foodNoise";
import { getResultsUniV5 } from "./resultFunctions/unifiedV5";

const assessmentService = axios.create({
  baseURL: window.wwBasePath.getAttribute("href"),
  timeout: 5000
});

const assessmentsService = axios.create({
  baseURL: window.wwBasePath.getAttribute("href"),
  timeout: 3000
});

const assessmentRecipeService = axios.create({
  baseURL: window.wwBasePath.getAttribute("href"),
  timeout: 5000
});

const assessmentCoachService = axios.create({
  baseURL: window.wwBasePath.getAttribute("href"),
  timeout: 5000
});

const assessmentProfileService = axios.create({
  baseURL: window.wwBasePath.getAttribute("href"),
  timeout: 5000
});

const sequenceService = axios.create({
  baseURL: window.ww.sequenceBaseUrl,
  timeout: 5000
});

const smsService = axios.create({
  baseURL: window.ww.wwAPI,
  timeout: 5000
});

function fetchAssessments([, slugs]) {
  const url = `/api/assessments?include=${slugs}`;
  return assessmentsService.get(url);
}

export function fetchAssessmentClinicalEligibility(assessmentId) {
  return assessmentService.post(`/api/assessments/clinical/eligibility`, {
    assessmentId
  });
}

export function fetchRecipes([, cuisines, dietaryPreferences]) {
  const url = `/api/recipe-filter`;
  return assessmentRecipeService.get(url, {
    params: { cuisines, dietaryPreferences }
  });
}

export function useFetchRecipes(cuisines, dietaryPreferences) {
  return useSWRImmut(["recipes", cuisines, dietaryPreferences], fetchRecipes);
}

export async function fetchCoaches(coachingStyles) {
  const url = `/api/coach-filter`;
  return await assessmentCoachService.get(url, {
    params: { coachingStyles, market: window.ww.locale }
  });
}

export async function fetchProfile() {
  const url = "/api/profile";
  return assessmentProfileService.post(url);
}

export async function fetchClinicalEligibility([, locale]) {
  const { uuid, token } = getSessionData();

  logger("Fetching clinical eligibility", {
    level: "info"
  });

  const clinicalEligibility = await smsService.post(
    `/sms/v3/subscriptions/clinical/eligibility?locale=${locale}${uuid ? `&uuid=${uuid}` : ""}`,
    null,
    {
      headers: {
        Authorization: `Bearer ${token}`
      }
    }
  );

  logger("Succesfully fetched clinical eligibility", {
    level: "info"
  });

  return clinicalEligibility;
}

export function useFetchClinicalEligibility(locale) {
  return useSWR(["clinicalEligibility", locale], fetchClinicalEligibility, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    shouldRetryOnError: false
  });
}

export function useAssessments(slugs) {
  slugs = slugs.join();
  return useSWR(["assessments", slugs], fetchAssessments);
}

function sanitizeAnswers(answers, questions) {
  const sanitizedAnswers = cloneDeep(answers);

  sanitizedAnswers.forEach(answersPerPage => {
    answersPerPage.answers.forEach(answer => {
      if (answer.id) {
        delete answer.id;
      }
    });

    const question = questions.find(q => q._id === answersPerPage.questionId);
    answersPerPage.questionHeaderText1 = question?.headerText1Analytics
      ? question.headerText1Analytics
      : "";
  });

  return sanitizedAnswers;
}

const removeSensitiveData = answersArray => {
  const clonedAnswersArray = cloneDeep(answersArray);
  clonedAnswersArray.forEach(answer => {
    if (answer?.profile?.firstName) {
      delete answer.profile.firstName;
    }
    if (answer?.profile?.lastName) {
      delete answer.profile.lastName;
    }
    if (answer?.profile?.uuid) {
      delete answer.profile.uuid;
    }
    if (answer?.profile?.medical?.email) {
      delete answer.profile.medical.email;
    }
  });

  return clonedAnswersArray;
};

export async function submitDataToDb(answers, questions, slugs) {
  try {
    const cleanedAnswers = removeSensitiveData(answers);

    const response = await assessmentService.post(
      "/api/assessments/new-quiz/save/v2",
      {
        answers: sanitizeAnswers(cleanedAnswers, questions),
        slugs,
        result: null,
        finished: false
      }
    );

    return response?.data?.id;
  } catch (error) {
    logger("Error saving data to DB", {
      level: "error",
      customAttributes: { error }
    });
  }
}

function getSuggestedProduct(newQuiz, answers, questions) {
  if (
    newQuiz.eli ||
    newQuiz.isSequence ||
    newQuiz.isUni ||
    newQuiz.isUni3 ||
    newQuiz.isUni4 ||
    newQuiz.isUni5 ||
    newQuiz.isClinical ||
    newQuiz.isFoodNoise
  ) {
    return defaultResultsFunction();
  }

  const resultsFuncName = getConfigByLocale(window.ww.locale)[
    "guidedSellingResultsFunction"
  ];

  const funcNameToFuncMapping = {
    guidedSellingAU,
    guidedSelling
  };

  return funcNameToFuncMapping[resultsFuncName](answers, questions);
}

export function saveUuid({ assessmentId, uuid }) {
  return assessmentService.post("/api/assessments/update-uuid", {
    assessmentId,
    uuid
  });
}

// A lack of waitlistReason means the user is eligible.
export async function fetchWaitlistReason(
  assessmentId,
  shouldAllow27to30BmiNoComorbidity = "false"
) {
  try {
    if (!assessmentId) {
      const msg = "Error: no aid found when getting waitlist reason";
      logger(msg, {
        level: "error"
      });
      throw new Error(msg);
    }
    const { token } = getSessionData();

    logger("Getting waitlist reason", {
      level: "info"
    });

    const eligibilityResponse = await sequenceService.get(
      `/api/v1/ww/waitlist-reason?wwAssessmentId=${assessmentId}&shouldAllow27to30BmiNoComorbidity=${shouldAllow27to30BmiNoComorbidity}`,
      {
        headers: {
          Authorization: `Bearer ${token}`
        }
      }
    );

    logger("Successfully got waitlist reason", {
      level: "info",
      customAttributes: { assessmentId }
    });

    return eligibilityResponse?.data?.waitlistReason;
  } catch (error) {
    logger("Error getting waitlist reason", {
      level: "info",
      customAttributes: { error }
    });
    return WAITLIST_REASON_IRRETRIEVABLE;
  }
}

export function useFetchAssessmentClinicalEligibility() {
  const MODE_PRISTINE = "PENDING";
  const MODE_SUBMITTING = "SUBMITTING";
  const MODE_SUCCESS = "SUCCESS";
  const MODE_FAILURE = "FAILURE";
  const [mode, setMode] = useState(MODE_PRISTINE);
  const [info, setInfo] = useState(null);

  const getAssessmentClinicalEligibility = useCallback(
    async ({ assessmentId }) => {
      setMode(MODE_SUBMITTING);

      try {
        const response = await fetchAssessmentClinicalEligibility(assessmentId);

        if (response.status !== 200) {
          throw new Error("Error fetching existing seqAssessmentId");
        }

        const { data } = response;

        setInfo(data);
        setMode(MODE_SUCCESS);
      } catch (error) {
        logger("Error fetching existing seqAssessmentId", {
          level: "error",
          customAttributes: { error }
        });
        setMode(MODE_FAILURE);
      }
    },
    []
  );

  return {
    getAssessmentClinicalEligibility,
    info,
    isSubmitting: mode === MODE_SUBMITTING,
    isSuccess: mode === MODE_SUCCESS,
    isFailure: mode === MODE_FAILURE,
    isPristine: mode === MODE_PRISTINE
  };
}

export function useSubmitAssessment() {
  const MODE_PRISTINE = "PENDING";
  const MODE_SUBMITTING = "SUBMITTING";
  const MODE_SUCCESS = "SUCCESS";
  const MODE_FAILURE = "FAILURE";
  const [assessmentId, setAssessmentId] = useState(null);
  const [suggestedProduct, setSuggestedProduct] = useState();
  const [mode, setMode] = useState(MODE_PRISTINE);
  const [eligibleSequence, setEligibleSequence] = useState(false);
  const [resultsScreen, setResultsScreen] = useState("");

  const submit = useCallback(
    async ({
      answers,
      questions,
      newQuiz,
      userPredictedToReachGoal = false,
      userRecommendedPlanLength = false,
      createIneligibleUser = () => {},
      profile,
      market
    }) => {
      setMode(MODE_SUBMITTING);
      try {
        const product = getSuggestedProduct(newQuiz, answers, questions);

        let sequenceEligible = false;
        let result = "";
        let foodNoiseUuid = "";
        const allow27to30Bmi = !profile?.isB2B;
        let previousEligibility, previousResultsScreen, previousAssessmentId;

        if (newQuiz.isUni3) {
          sequenceEligible = getIsClinicEligibleUni3(answers, questions);
          result = getResultsPageUniV3(answers, questions);
        } else if (newQuiz.isUni4) {
          sequenceEligible = getIsClinicEligibleUni4(
            answers,
            questions,
            profile
          );
          result = getResultsPageUniV4(answers, questions, profile);
        } else if (newQuiz.isUni5) {
          ({ result, sequenceEligible } = await getResultsUniV5(
            answers,
            questions,
            profile,
            newQuiz.dbSlugs
          ));
        } else if (newQuiz.isUni) {
          sequenceEligible = getIsClinicEligibileUni(answers, questions);
          result = getResultsPageUni(answers, questions);
        } else if (newQuiz.eli) {
          const id = await submitDataToDb(answers, questions, newQuiz.dbSlugs);
          const waitlistReason = await fetchWaitlistReason(id, allow27to30Bmi);

          sequenceEligible = !waitlistReason;
          const wwEligible = isEligibleWw(answers, profile, questions);

          result = getResultsPageEli(
            waitlistReason,
            sequenceEligible,
            wwEligible
          );
        } else if (newQuiz.isSequence) {
          const id = await submitDataToDb(answers, questions, newQuiz.dbSlugs);
          const waitlistReason = await fetchWaitlistReason(id, allow27to30Bmi);

          sequenceEligible = !waitlistReason;
          const wwEligible = isEligibleWw(answers, profile, questions);

          result = getResultsJoinSequence(
            waitlistReason,
            sequenceEligible,
            wwEligible
          );
        } else if (newQuiz.isClinical) {
          const id = await submitDataToDb(answers, questions, newQuiz.dbSlugs);
          const waitlistReason = await fetchWaitlistReason(id, allow27to30Bmi);

          if (profile?.seqAssessmentId) {
            const { data } = await fetchAssessmentClinicalEligibility(
              profile?.seqAssessmentId
            );

            // Check if the user was previously ineligible or eligible
            // and if they took the assessment less than 30 days ago.
            if (data?.info?.endTime && data?.info?.result === FINISHED) {
              const thirtyDaysAgo = new Date(
                Date.now() - SECONDS_IN_THIRTY_DAYS * 1000
              );
              const assessmentDate = new Date(data.info.endTime);

              if (assessmentDate > thirtyDaysAgo) {
                previousEligibility = data?.info.eligibleSequence;
                previousResultsScreen = data?.info.resultsScreen;
                previousAssessmentId = data?.assessmentId;
              }
            }
          }

          sequenceEligible = !waitlistReason;
          result = waitlistReason;
        } else if (newQuiz.isFoodNoise) {
          const foodNoiseResults = getResultsPageFoodNoise(
            answers,
            questions,
            profile
          );
          result = {
            sum: foodNoiseResults.sum,
            score: foodNoiseResults.score,
            isActive: foodNoiseResults.isActive,
            emailStatus: foodNoiseResults.emailStatus
          };

          foodNoiseUuid = foodNoiseResults.uuid;
        }

        // If someone has a previous assessment id (another quiz taken within the last 30 days),
        // show the results from the previous assessment.
        // However, still store the results from the current assessment in the database.
        setEligibleSequence(previousEligibility || sequenceEligible);
        setResultsScreen(previousResultsScreen || result);

        const response = await assessmentService.post(
          "/api/assessments/new-quiz/save/v2",
          {
            answers: sanitizeAnswers(removeSensitiveData(answers), questions),
            slugs: newQuiz.dbSlugs,
            userPredictedToReachGoal,
            userRecommendedPlanLength,
            resultsScreen: result,
            eligibleSequence: sequenceEligible,
            result: product,
            finished: true,
            foodNoiseUuid
          }
        );

        const { status, data } = response;
        const aid = data["id"];

        if (status !== 200) {
          throw new Error();
        }

        setSuggestedProduct(product);
        setAssessmentId(previousAssessmentId || aid);

        // Logged in quizzes - update user profile seqAssessmentId.
        if (
          newQuiz.isUni4 ||
          newQuiz.isUni3 ||
          newQuiz.isSequence ||
          newQuiz.isClinical
        ) {
          const { uuid } = getSessionData();

          if (!aid) {
            logger("Error: no aid found when submitting assessment", {
              level: "error"
            });

            if (newQuiz.isClinical) {
              throw new Error("No aid found when submitting assessment");
            }
          }

          // If the user already has a previous assessment id
          // (another quiz taken within the last 30 days),
          // Do not update the seqAssessmentId in their user profile.
          if (uuid && aid && !previousAssessmentId) {
            const assessmentIdUpdated = await updateAssessmentId({
              seqAssessmentId: aid,
              market
            });

            if (assessmentIdUpdated?.status !== 200 && newQuiz.isClinical) {
              throw new Error("Error updating assessment id");
            }
          }
        }

        // Create an ineligible user account for sequence quiz only.
        // If the user already has a previous assessment id,
        // (another quiz taken within the last 30 days)
        // do not create an ineligible user account.
        if (
          !sequenceEligible &&
          (newQuiz.isSequence || newQuiz.isClinical) &&
          aid &&
          !previousAssessmentId
        ) {
          createIneligibleUser(aid);
        }

        setMode(MODE_SUCCESS);
      } catch (error) {
        logger("Error submitting assessment", {
          level: "error",
          customAttributes: { error }
        });
        setMode(MODE_FAILURE);
      }
    },
    []
  );

  return {
    assessmentId,
    suggestedProduct,
    submit,
    isSubmitting: mode === MODE_SUBMITTING,
    isSuccess: mode === MODE_SUCCESS,
    isFailure: mode === MODE_FAILURE,
    isPristine: mode === MODE_PRISTINE,
    eligibleSequence,
    resultsScreen
  };
}
